import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthorization } from 'contexts/AuthContextProvider';
import { useService } from 'contexts/ServiceContextProvider';
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import { useSnackbar } from 'contexts/SnackbarContextProvider';
import { useParams } from "react-router-dom";
import {
    Avatar,
    Checkbox,
    AvatarGroup,
    Badge,
    Box,
    Button,
    CardContent,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    TextField,
    Typography,
    Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import NewTaskModal from 'components/Modals/CreateNewTask';
import ArchiveTaskModal from 'components/Modals/ArchiveTask';
import ArchiveListModal from 'components/Modals/ArchiveList';
import ViewTaskModal from 'components/Modals/ViewTask';
import ListHeaderModal from 'components/Modals/ListHeader';
import ListFiltersModal from '../components/Modals/ListFiltersModal';
import NewListModal from 'components/Modals/NewListModal';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SubjectIcon from '@mui/icons-material/Subject';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ListIcon from '@mui/icons-material/List';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MessageIcon from '@mui/icons-material/Message';
import {
    closestCenter,
    closestCorners,
    DndContext,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';
import BoardSkeleton from './Skeletons/BoardSkeleton';
import { useTranslation } from "react-i18next";
import { useProjects } from 'contexts/ProjectsContextProvider';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArchiveIcon from '@mui/icons-material/Archive';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import FilterListIcon from '@mui/icons-material/FilterList';
import '../css/views/ProjectPage.css';
import { getTextColor } from '../utils/ColorUtils.jsx'
import ScrollAwareTooltip from 'components/Custom/ScrollAwareTooltip';


const ProjectPage = () => {
    const { t } = useTranslation();
    const [openAddListModal, setOpenAddListModal] = useState(false);

    const { projectId } = useParams();

    const { getFilteredAndSortedList, GetTasksFilteredForList, getTasksFilters, GetTasksForList, UpdateLabel, UpdateBoardListsDragging, UpdateTaskDragging, UpdateLabelTitle, DeleteLabel, AddLabelToBoard,
        UpdateListCollapsable, UpdateListColor, getUserTimeZone, CreateNewTask, GetProjectBoardInformation, UpdateList, ArchiveList,
        UpdateTask, ArchiveTask, toggleTaskCount, ToggleFullLabel, getProjectWorkers, WhoAmI, CheckTaskExistenceInBoard, CreateNewList } = useService();

    const [myBoard, setMyBoard] = useState(null);
    const [openNewTaskModal, setOpenNewTaskModal] = useState(false);
    const [openArchiveTaskModal, setOpenArchiveTaskModal] = useState(false);
    const [openArchiveListModal, setOpenArchiveListModal] = useState(false);
    const [openListHeaderModal, setOpenListHeaderModal] = useState(false);
    const [showColorOptions, setShowColorOptions] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [avoidMisfire, setAvoidMisfire] = useState(false);
    const [activeList, setActiveList] = useState('');
    const [contextTaskMenuPosition, setContextTaskMenuPosition] = useState({ x: 0, y: 0 });
    const [contextListMenuPosition, setContextListMenuPosition] = useState({ x: 0, y: 0 });
    const [selectedTask, setSelectedTask] = useState(null);
    const [modalSelectedTask, setModalSelectedTask] = useState([null]);
    const [projectWorkers, setProjectWorkers] = useState([null]);
    const [selectedList, setSelectedList] = useState([null]);
    const [editTaskMode, setEditTaskMode] = useState(null);
    const [editListMode, setEditListMode] = useState(null);
    const [editTaskNewTitle, setEditTaskNewTitle] = useState(null);
    const [editListNewTitle, setEditListNewTitle] = useState("");
    const [titleError, setTitleError] = useState(false);
    const { getLoggedUser } = useAuthorization();
    const [myUserTimezone, setMyUserTimezone] = useState();
    const [myUserInMemberFormat, setMyUserInMemberFormat] = useState();
    const [collapsedLists, setCollapsedLists] = useState({});
    const [collapsing, setCollapsing] = useState({});
    const [viewTaskModalOpen, setViewTaskModalOpen] = useState(false);
    const [autoFocusLabel, setAutoFocusLabel] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [, setTaskToOpen] = useState(true);
    const [taskToScrollTo, setTaskToScrollTo] = useState(true);
    const [pendingScrollTaskId, setPendingScrollTaskId] = useState(null);
    const [pendingCreateTask, setPendingCreateTask] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
    const [openListFiltersModal, setOpenListFiltersModal] = useState(false);
    const [filters, setFilters] = useState([null]);
    const [taskFilters, setTaskFilters] = useState({});
    const [filteredLists, setFilteredLists] = useState({});
    const [listSortState, setListSortState] = useState({});
    const [hashHandled, setHashHandled] = useState(false);
    const { showSnackbar } = useSnackbar();
    const [openCover, setCoverModalOpen] = useState(false);
    const [highlightedTaskId, setHighlightedTaskId] = useState(null);
    const [isOverlayVisible, setOverlayVisible] = useState(false);
    const containerRef = useRef(null);
    const [shouldRenderOverlay, setShouldRenderOverlay] = useState(false);
    const workspaceContext = useWorkspaces();
    const myWorkspaces = workspaceContext.workspaces;
    const workspaceWorkers = workspaceContext.workspaceWorkers;
    const getCurrentlyLoggedUser = workspaceContext.getCurrentlyLoggedUser;
    const projectContext = useProjects();
    const myCurrentProject = projectContext.currentProject;
    const [, setTaskPages] = useState({});
    const taskPagesRef = useRef({});
    const scrollHandlersRef = useRef({});
    const isLoadingPage = useRef({});
    const myBoardRef = useRef(myBoard);
    const myWorkspacesRef = useRef(myWorkspaces);

    useEffect(() => {
        myBoardRef.current = myBoard;
    }, [myBoard]);

    useEffect(() => {
        myWorkspacesRef.current = myWorkspaces;
    }, [myWorkspaces]);

    const listRefs = useRef({});
    const hasMorePages = useRef({});

    const handleOpenAddListModal = () => setOpenAddListModal(true);

    const handleCloseAddListModal = () => {
        setOpenAddListModal(false);
    };

    const handleAddList = async (title) => {
        if (title.trim() === "") return;
        setAvoidMisfire(true);
        try {
            const project_id = myBoard.project;
            const board_id = myBoard.id;
            const response = await CreateNewList(myWorkspaces[0].id, project_id, title, board_id);
            if (response.status === 201) {
                const newList = {
                    ...response.data,
                    tasks: []
                };
                setMyBoard(prevBoard => ({
                    ...prevBoard,
                    lists: [...prevBoard.lists, newList]
                }));
            }
        } catch (error) {
            console.error('Creating List Failed:', error.message);
        } finally {
            setAvoidMisfire(false);
            setOpenAddListModal(false);
        }
    };



    /* eslint-disable react-hooks/exhaustive-deps */
    const loadNextPage = useCallback(async (listId, currentNumberOfTasksLoaded) => {
        if (isLoadingPage.current[listId] || hasMorePages.current[listId] === false) return;
        isLoadingPage.current[listId] = true;

        const currentPage = taskPagesRef.current[listId] || 1;
        const nextPage = currentPage + 1;
        let updatedTasks = [];
        let response = null;

        try {
            if (filteredLists[listId]) {
                response = await GetTasksFilteredForList(
                    myWorkspacesRef.current[0].id,
                    myBoardRef.current.project,
                    listId,
                    { limit: 100, offset: currentNumberOfTasksLoaded, taskFilters: taskFilters[listId], sort: listSortState[listId] || 'order' }
                );
                updatedTasks = response.data.results;
            } else {
                response = await GetTasksForList(
                    myWorkspacesRef.current[0].id,
                    myBoardRef.current.project,
                    listId,
                    { limit: 100, offset: currentNumberOfTasksLoaded, sort: listSortState[listId] || 'order' }
                );
                updatedTasks = response.data.results;
            }



            // Filter out tasks that already exist
            setMyBoard((prevBoard) => {
                const updatedLists = prevBoard.lists.map((list) => {
                    if (list.id === listId) {
                        // Create a Set of existing task IDs for quick lookup
                        const existingTaskIds = new Set(list.tasks.filter(task => task !== null).map(task => task.id));

                        // Filter out duplicate tasks
                        const newUniqueTasks = updatedTasks.filter(task => !existingTaskIds.has(task.id));

                        const tasksWithPlaceholders = [...list.tasks];

                        const startIndex = tasksWithPlaceholders.filter(task => task !== null).length;

                        newUniqueTasks.forEach((task, index) => {
                            tasksWithPlaceholders[startIndex + index] = task;
                        });

                        return {
                            ...list,
                            tasks: tasksWithPlaceholders,
                        };
                    }
                    return list;
                });
                return { ...prevBoard, lists: updatedLists };
            });

            // Update pagination info
            setTaskPages((prevPages) => ({ ...prevPages, [listId]: nextPage }));
            taskPagesRef.current = { ...taskPagesRef.current, [listId]: nextPage };
            hasMorePages.current[listId] = response.data.next !== null;

        } catch (error) {
            console.error('Failed to load more tasks:', error);
            hasMorePages.current[listId] = false;
        } finally {
            isLoadingPage.current[listId] = false;

            setTimeout(() => {
                const container = listRefs.current[listId];
                if (container) {
                    handleScroll(listId);
                }
            }, 0);
        }
    }, [filteredLists, taskFilters, listSortState]);

    const thresholds = useRef({}); // Store thresholds dynamically for each 

    const handleScroll = useCallback(async (listId) => {
        if (window.location.hash) return;

        const container = listRefs.current[listId];
        if (container) {
            const list = myBoardRef.current.lists.find(list => list.id === listId);
            if (!list) return;

            const scrollPosition = container.scrollTop + container.clientHeight;
            const totalHeight = container.scrollHeight;
            const totalTasks = list.task_count || 1;
            const tasksLoaded = list.tasks.filter(task => task !== null).length || 1;
            const tasksRemaining = totalTasks - tasksLoaded;

            let updatedTasks = [];

            const iterations = Math.floor(tasksLoaded / 100);

            const block_threshold_per_page_for_list = totalHeight / (totalTasks / 100) * 0.9;
            const currentThreshold = Math.ceil(totalHeight - (block_threshold_per_page_for_list * iterations));
            const maxPossibleThreshold = totalHeight - block_threshold_per_page_for_list;

            if (!thresholds.current[listId]) {
                thresholds.current[listId] = maxPossibleThreshold
            }

            const remainingScrollHeight = totalHeight - scrollPosition;

            const scrollRatio = scrollPosition / totalHeight;

            if (scrollRatio > 0.7 && tasksRemaining > 0 && !isLoadingPage.current[listId]) {
                // Perform one large request to load all remaining tasks
                // Since we know tasksRemaining, we can request them all.
                const limit = Math.min(tasksRemaining, 500);

                try {
                    isLoadingPage.current[listId] = true;
                    if (filteredLists[listId]) {
                        const response = await GetTasksFilteredForList(
                            myWorkspacesRef.current[0].id,
                            myBoardRef.current.project,
                            listId,
                            { limit: limit, offset: tasksLoaded, taskFilters: taskFilters[listId], sort: listSortState[listId] || 'order' }
                        );
                        updatedTasks = response.data.results;

                    } else {
                        const response = await GetTasksForList(
                            myWorkspacesRef.current[0].id,
                            myBoardRef.current.project,
                            listId,
                            { limit: limit, offset: tasksLoaded, sort: listSortState[listId] }
                        );
                        updatedTasks = response.data.results;
                    }

                    setMyBoard((prevBoard) => {
                        const updatedLists = prevBoard.lists.map((listItem) => {
                            if (listItem.id === listId) {
                                const tasksWithPlaceholders = [...listItem.tasks];
                                const startIndex = tasksWithPlaceholders.filter(task => task !== null).length;

                                updatedTasks.forEach((task, index) => {
                                    tasksWithPlaceholders[startIndex + index] = task;
                                });

                                return {
                                    ...listItem,
                                    tasks: tasksWithPlaceholders,
                                };
                            }
                            return listItem;
                        });
                        return { ...prevBoard, lists: updatedLists };
                    });

                    hasMorePages.current[listId] = false; // We just loaded all remaining tasks
                } catch (error) {
                    console.error("Failed to load all remaining tasks:", error);
                } finally {
                    isLoadingPage.current[listId] = false;
                    setTimeout(() => {
                        const container = listRefs.current[listId];
                        if (container) {
                            // Force scroll to the bottom
                            container.scrollTop = container.scrollHeight;
                        }
                    }, 0);
                    if (pendingScrollTaskId) {
                        const anyListLoading = Object.values(isLoadingPage.current).some(isLoading => isLoading);
                        if (!anyListLoading) {
                            setTaskToScrollTo(pendingScrollTaskId);
                            setPendingScrollTaskId(null);
                        }
                    }
                }
            }


            if (remainingScrollHeight < currentThreshold) {
                if (!isLoadingPage.current[listId]) {
                    loadNextPage(listId, tasksLoaded);
                    thresholds.current[listId] = currentThreshold; // Update the threshold for this list
                }
            }
        }
    }, [loadNextPage, filteredLists, listSortState]);

    // When isOverlayVisible changes
    useEffect(() => {
        if (isOverlayVisible) {
            setShouldRenderOverlay(true);
        } else {
            const timer = setTimeout(() => setShouldRenderOverlay(false), 500); // 500ms matches the transition time
            return () => clearTimeout(timer);
        }
    }, [isOverlayVisible]);

    const openViewTaskModalHandler = (task, list, autoFocus = false, open_cover = false) => {
        handleTaskCloseMenu();
        setSelectedTask(task);
        setSelectedList(list);
        setActiveList(list.id);
        setAutoFocusLabel(autoFocus);
        setCoverModalOpen(open_cover);
        setViewTaskModalOpen(true);
    };

    useEffect(() => {
        const fetchUserTimezone = async () => {
            try {
                const myTime = await getUserTimeZone(getLoggedUser());
                setMyUserTimezone(myTime)
            } catch (error) {
            }
        };
        if (getUserTimeZone && getLoggedUser) {
            fetchUserTimezone();
        }
    }, [getUserTimeZone, getLoggedUser]);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            const user = await WhoAmI();
            setMyUserInMemberFormat(user);
        };

        if (!myUserInMemberFormat) {
            fetchCurrentUser();
        }
    }, [myUserInMemberFormat, WhoAmI]);

    useEffect(() => {
        if (myCurrentProject) {
            document.title = myCurrentProject + " - Workflow PRO";
        }
    }, [myCurrentProject]);

    useEffect(() => {
        if (modalSelectedTask[0] !== null) {
            setViewTaskModalOpen(true);
        }
    }, [modalSelectedTask]);

    useEffect(() => {
        document.addEventListener('contextmenu', handleContextMenu);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
        };
    });

    const handleTaskModalOpen = (list) => {
        setActiveList(list.id);
        setOpenNewTaskModal(true);
    };

    const handleTaskModalClose = () => {
        setOpenNewTaskModal(false);
        setTimeout(() => {
            if (document.activeElement) {
                document.activeElement.blur();
            }
            document.body.focus();
        }, 50);
    };

    const handleArchiveTaskModalOpen = () => {
        setOpenArchiveTaskModal(true);
    };

    const handleArchiveListModalOpen = () => {
        setOpenArchiveListModal(true);
    };

    const handleToggleTaskCount = (list_id, new_bool) => {
        toggleTaskCount(myWorkspaces[0].id, myBoard.project, list_id, new_bool)
            .then(() => {
                const updatedLists = myBoard.lists?.map(list => {
                    if (list.id === list_id) {
                        return { ...list, show_total_tasks: new_bool };
                    }
                    return list;
                });

                setMyBoard(prevBoard => ({ ...prevBoard, lists: updatedLists }));

                // Update selectedList if it matches the toggled list
                if (selectedList && selectedList.id === list_id) {
                    setSelectedList(prevSelected => ({
                        ...prevSelected,
                        show_total_tasks: new_bool
                    }));
                }
            })
            .catch(error => {
                return;
            });
    };

    const handleListHeader = () => {
        handleListCloseMenu();
        setOpenListHeaderModal(true);
    };

    const handleListHeaderModalClose = (headerData = null) => {
        setOpenListHeaderModal(false);
        if (headerData) {
            selectedList.header.title = headerData.title;
            selectedList.header.color = headerData.color;
            selectedList.header.emoji = headerData.emoji;
            selectedList.header.title_color = headerData.title_color;
            selectedList.header.image = headerData.image;
        }
    };

    const handleArchiveTaskModalClose = () => {
        setOpenArchiveTaskModal(false);
        handleTaskCloseMenu();
    };

    const handleMenuOpen = (event, list) => {
        setAnchorEl(event.currentTarget);
        setSelectedList(list);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSubmenuAnchorEl(null);
    };

    const handleSubmenuOpen = (event) => {
        setSubmenuAnchorEl(event.currentTarget);
    };

    const handleSubmenuClose = () => {
        setSubmenuAnchorEl(null);
    };

    const handleOpenFiltersModal = async () => {
        try {
            const response = await getTasksFilters(
                myWorkspacesRef.current[0].id,
                myBoardRef.current.project,
                selectedList.id,
                { fields: ["label", "members"] }
            );

            setFilters(response.data);
            setFilteredLists((prev) => ({ ...prev, [selectedList.id]: true }));

        } catch (error) {
            console.error("Failed to load all remaining tasks:", error);
        }


        setOpenListFiltersModal(true);
        handleMenuClose();
    };

    const handleCloseFiltersModal = (listData = null, selectedFilters = [], searching = null) => {
        setOpenListFiltersModal(false);

        if (searching) {
            setTaskFilters((prevState) => ({
                ...prevState,
                [selectedList.id]: selectedFilters
            }));

            // Create a new boardData copy
            let updatedBoardData = { ...myBoard };

            if (listData) {
                const updatedSelectedList = updateListWithPlaceholders({
                    ...selectedList,
                    task_count: listData.count,
                    tasks: listData.results,
                });

                if (listData.count > listData.results.length) {
                    hasMorePages.current[selectedList.id] = true;
                }

                const selectedListIndex = updatedBoardData.lists.findIndex(
                    (list) => list.id === selectedList.id
                );
                if (selectedListIndex !== -1) {
                    updatedBoardData.lists[selectedListIndex] = updatedSelectedList;
                }
                setListSortState((prevState) => ({
                    ...prevState,
                    [selectedList.id]: 'order'
                }));
            }

            setMyBoard(updatedBoardData);
        } else {
            setFilteredLists((prev) => ({ ...prev, [selectedList.id]: false }));
        }
    };

    const handleListSort = async (sortBy) => {
        try {
            const listId = selectedList.id;

            const currentSort = listSortState[listId];

            let newSort;
            if (currentSort === `-${sortBy}`) {
                newSort = "order"; // Reset to 'order' if the current sort is negative
            } else if (currentSort === sortBy) {
                newSort = `-${sortBy}`; // Toggle to descending if currently ascending
            } else {
                newSort = sortBy; // Default to ascending for a new field
            }

            const formattedSort = { sort: newSort };


            const combinedParams = { ...taskFilters[listId], ...formattedSort };


            const response = await getFilteredAndSortedList(
                myWorkspacesRef.current[0].id,
                myBoardRef.current.project,
                listId,
                combinedParams
            );

            // Update the list with placeholders
            const updatedList = updateListWithPlaceholders({
                ...selectedList,
                task_count: response.count,
                tasks: response.results,
            });

            selectedList.tasks = updatedList.tasks;

            if (response.count > response.results.length) {
                hasMorePages.current[selectedList.id] = true;
            }

            setListSortState((prevState) => ({
                ...prevState,
                [listId]: newSort
            }));
        } catch (error) {
            console.error("Failed to load sorted tasks:", error);
        }

        handleMenuClose(null);
    };

    useEffect(() => {
        const performScroll = () => {
            const taskElement = document.getElementById(`${taskToScrollTo}`);
            if (taskElement) {
                taskElement.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });

                setTimeout(() => {
                    setHashHandled(false);
                    setTaskToScrollTo(null);
                }, 1500);
            } else {
                setTaskToScrollTo(null);
            }
        };

        if (taskToScrollTo) {
            const anyListLoading = Object.values(isLoadingPage.current).some(isLoading => isLoading);
            if (anyListLoading) {
                const interval = setInterval(() => {
                    const stillLoading = Object.values(isLoadingPage.current).some(isLoading => isLoading);
                    if (!stillLoading) {
                        clearInterval(interval);
                        performScroll();
                    }
                }, 100);

                const timeout = setTimeout(() => {
                    clearInterval(interval);
                    console.warn("Loading did not finish in time for scrolling.");
                    setTaskToScrollTo(null);
                }, 10000);

                return () => {
                    clearInterval(interval);
                    clearTimeout(timeout);
                };
            } else {
                performScroll();
            }
        }
    }, [taskToScrollTo]);

    useEffect(() => {
        if (pendingScrollTaskId) {
            const checkAndScroll = () => {
                const anyListLoading = Object.values(isLoadingPage.current).some(isLoading => isLoading);
                if (!anyListLoading) {
                    setTaskToScrollTo(pendingScrollTaskId);
                    setPendingScrollTaskId(null);
                }
            };

            const interval = setInterval(checkAndScroll, 100);
            const timeout = setTimeout(() => {
                clearInterval(interval);
                console.warn("Loading did not finish in time for pending scroll.");
                setPendingScrollTaskId(null);
            }, 5000); // 5 seconds

            return () => {
                clearInterval(interval);
                clearTimeout(timeout);
            };
        }
    }, [pendingScrollTaskId]);

    const handleSaveTask = async (nameForCard, selectedLabels, pastedCover) => {
        setAvoidMisfire(true);
        const anyListLoading = Object.values(isLoadingPage.current).some(isLoading => isLoading);

        if (anyListLoading) {
            showSnackbar(t("project_page.loading_tasks_first"), "info");
            setPendingCreateTask({ nameForCard, selectedLabels, pastedCover });
        } else {
            await handleCreateTask(nameForCard, selectedLabels, pastedCover);
        }

        setAvoidMisfire(false);
        handleTaskModalClose();
    };

    const handleCreateTask = async (nameForCard, selectedLabels, pastedCover) => {
        try {
            const response = await CreateNewTask(
                myWorkspaces[0].id,
                myBoard.project,
                nameForCard,
                activeList,
                selectedLabels,
                pastedCover
            );

            if (response.status === 201) {
                const newTask = response.data;
                setMyBoard((prevBoard) => {
                    const updatedLabels = prevBoard.labels.map((label) => {
                        if (selectedLabels.includes(label.id)) {
                            return { ...label, usage_count: label.usage_count + 1 };
                        }
                        return label;
                    });

                    const updatedLists = prevBoard.lists.map((list) => {
                        if (list.id === activeList) {
                            const updatedTasks = [newTask, ...list.tasks];
                            return {
                                ...list,
                                tasks: updatedTasks,
                                task_count: list.task_count + 1,
                            };
                        }
                        return list;
                    });

                    return {
                        ...prevBoard,
                        labels: updatedLabels,
                        lists: updatedLists,
                    };
                });

                showSnackbar(t("modal.task_created"), "success");

                const anyListLoadingNow = Object.values(isLoadingPage.current).some(isLoading => isLoading);
                if (anyListLoadingNow) {
                    setPendingScrollTaskId(newTask.id);
                } else {
                    setTaskToScrollTo(newTask.id);
                }

            }
        } catch (error) {
            showSnackbar(t("modal.task_fail"), 'error');
            console.error('Creating Task Failed:', error.message);
        }
    };

    const currentWorkspace = useMemo(() => myWorkspaces[0], [myWorkspaces]);

    const updateListWithPlaceholders = (list) => {
        const totalTasks = list.task_count || 0;
        const loadedTasks = list.tasks || [];
        const tasksWithPlaceholders = Array(totalTasks).fill(null);

        loadedTasks.forEach((task, index) => {
            tasksWithPlaceholders[index] = task;
        });

        return {
            ...list,
            tasks: tasksWithPlaceholders,
        };
    };

    const fetchBoardData = useCallback(async () => {
        if (currentWorkspace) {
            try {
                const response = await GetProjectBoardInformation(projectId, currentWorkspace.id);
                if (response) {
                    const boardData = response.data;
                    const initialCollapsedState = {};
                    boardData.lists.forEach(list => {
                        initialCollapsedState[list.id] = list.collapsable;
                        hasMorePages.current[list.id] = list.next !== null;
                    });
                    boardData.lists = boardData.lists.map(updateListWithPlaceholders);
                    setMyBoard(boardData);
                    setCollapsedLists(initialCollapsedState);
                    const workersProject = await getProjectWorkers(currentWorkspace.id, boardData.project);
                    setProjectWorkers(workersProject);
                    setIsLoading(false);
                }
            } catch (error) {
                console.error("Error fetching project board:", error);
                setIsLoading(false);
            }
        }
    }, [projectId, currentWorkspace, GetProjectBoardInformation, getProjectWorkers]);


    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        fetchBoardData();
    }, [currentWorkspace]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (pendingCreateTask) {
            const checkLoadingAndCreateTask = () => {
                const anyListLoading = Object.values(isLoadingPage.current).some(isLoading => isLoading);
                if (!anyListLoading) {
                    handleCreateTask(
                        pendingCreateTask.nameForCard,
                        pendingCreateTask.selectedLabels,
                        pendingCreateTask.pastedCover
                    );
                    setPendingCreateTask(null);
                }
            };

            checkLoadingAndCreateTask();

            const interval = setInterval(checkLoadingAndCreateTask, 100);

            const timeout = setTimeout(() => {
                clearInterval(interval);
                showSnackbar(t("project_page.loading_timeout"), "error");
                setPendingCreateTask(null);
            }, 15000); // 15 seconds timeout (loading tasks)

            return () => {
                clearInterval(interval);
                clearTimeout(timeout);
            };
        }
    }, [pendingCreateTask, t]);

    /************************************************************
    * 1) NEW useEffect: Runs once myBoard is loaded & not handled
    ************************************************************/
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        // Only run if we haven't handled the hash yet, and myBoard has lists
        if (!hashHandled && window.location.hash.startsWith('#')) {
            //This means that the page was LOADED once and then added a hash, meaning we came from a notification or a search!.
            setHashHandled(false);
        }
        if (!hashHandled && myBoard && myBoard.lists && myBoard.lists.length > 0) {

            const hash = window.location.hash;
            if (hash.startsWith('#') && !isLoading) {

                // Use requestAnimationFrame so the DOM can finish rendering
                requestAnimationFrame(() => {
                    scrollToTaskByHash(myBoard, hash, setHighlightedTaskId, setOverlayVisible);
                });
                setHashHandled(true);
            }
        }
    }, [hashHandled, myBoard, isLoading, window.location.hash]);

    /************************************************************
     * 2) UPDATED scrollToTaskByHash: Uses a short "retry loop"
     *    so we wait for DOM nodes to actually be rendered.
     ************************************************************/
    async function scrollToTaskByHash(board, hash, setHighlightedTaskId, setOverlayVisible) {
        const taskId = parseInt(hash.slice(1), 10);


        let taskToScrollTo = null;
        let listOfTask = null;

        for (const list of board.lists) {
            const task = list.tasks.find((t) => t?.frontend_order === taskId);
            if (task) {
                taskToScrollTo = task;
                listOfTask = list;
                break;
            }
        }

        if (!taskToScrollTo) {
            showSnackbar("Searching and loading for the task, please wait...", "info");
            const task_exists = await CheckTaskExistenceInBoard(currentWorkspace.id, projectId, myBoard.id, window.location.hash);
            setHashHandled(false)

            if (task_exists) {

                fetchBoardData();
                return;
            } else {
                const cleanedUrl = `${window.location.origin}${window.location.pathname.replace(/\/$/, '')}`;
                window.history.replaceState(null, '', cleanedUrl);
                return;
            }
        }

        let attempts = 0;
        const MAX_ATTEMPTS = 10; //This is 10 reframes of the page, meaning there's no lying to this guy!
        //my computer never took more than 1 try, the tasks as far as I am aware are always rendered when calling a requestFrameAnimation.

        function tryScroll() {
            const taskElement = document.getElementById(String(taskToScrollTo.id));
            if (taskElement) {
                taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

                setHighlightedTaskId(taskToScrollTo.id);
                setOverlayVisible(true);

                setTimeout(() => {
                    setHashHandled(false);
                    setOverlayVisible(false);
                    setHighlightedTaskId(null);

                    const cleanedUrl = `${window.location.origin}${window.location.pathname.replace(/\/$/, '')}`;
                    window.history.replaceState(null, '', cleanedUrl);



                    openViewTaskModalHandler(taskToScrollTo, listOfTask);
                    setTaskToOpen(false);
                    return
                }, 1500);

                return;
            }

            if (attempts < MAX_ATTEMPTS) {
                attempts++;
                requestAnimationFrame(tryScroll);
            } else {
                console.warn("Could not find the task element after multiple frames.");
            }
        }
        requestAnimationFrame(tryScroll);
    }

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 0,
                distance: 10
            },
        }),
    );

    const handleDragStart = (event) => {
        const activeId = event.active.id;
        if (activeId.startsWith('placeholder-')) {
            return;
        }
        setActiveId(activeId);
        document.body.style.cursor = 'grabbing';
    };

    //This is a helper for the drags as its done in several places, drag, right click to top etc...
    const moveItem = (array, fromIndex, toIndex) => {
        const item = array[fromIndex];
        const updatedArray = [...array];
        updatedArray.splice(fromIndex, 1);
        updatedArray.splice(toIndex, 0, item);
        return updatedArray;
    };

    const handleDragEnd = async ({ active, over }) => {
        if (!over || active.id.startsWith('placeholder-') || over.id.startsWith('placeholder-')) {
            setActiveId(null);
            document.body.style.cursor = 'auto';
            return;
        }

        const activeId = active.id;
        const overId = over.id;

        // Handling list drag-and-drop
        if (activeId.startsWith('List-') && overId.startsWith('List-') && activeId !== overId) {
            // Find the indices of the active and over lists
            const activeIndex = myBoard.lists.findIndex(list => `List-${list.id}` === activeId);
            const overIndex = myBoard.lists.findIndex(list => `List-${list.id}` === overId);

            if (activeIndex === -1 || overIndex === -1) {
                console.error("Invalid indices for list move:", activeIndex, overIndex);
                return;
            }

            const updatedLists = moveItem(myBoard.lists, activeIndex, overIndex);

            updatedLists.forEach((list, index) => {
                list.order = index;
            });

            setMyBoard(prevBoard => ({
                ...prevBoard,
                lists: updatedLists
            }));

            await UpdateBoardListsDragging(myWorkspaces[0].id, myBoard.project, updatedLists, myBoard.id);

        } else {
            // Handling task drag-and-drop
            const activeContainerId = myBoard.lists.find(list => list.tasks.some(task => task?.id === activeId))?.id;
            const overContainerId = over.id.startsWith('List-') ? over.id.replace('List-', '') : myBoard.lists.find(list => list.tasks.some(task => task?.id === overId))?.id;

            if (!activeContainerId || !overContainerId) return;

            const activeTask = myBoard.lists.flatMap(list => list.tasks).find(task => task?.id === activeId);

            if (activeContainerId === overContainerId) {
                // Moving tasks within the same list
                const newPosition = over.data.current?.sortable.index;
                const activeList = myBoard.lists.find(list => list.id === activeContainerId);
                const oldIndex = activeList.tasks.findIndex(task => task?.id === activeId);

                if (oldIndex !== -1 && oldIndex !== newPosition) {
                    // Reorder tasks within the same list
                    const updatedTasks = moveItem(activeList.tasks, oldIndex, newPosition);

                    // Update the state immediately
                    const updatedLists = myBoard.lists.map(list =>
                        list.id === activeContainerId ? { ...list, tasks: updatedTasks } : list
                    );

                    setMyBoard(prevBoard => ({
                        ...prevBoard,
                        lists: updatedLists
                    }));
                }

                // Then make the API call
                await UpdateTaskDragging(
                    myWorkspaces[0].id,
                    myBoard.project,
                    activeTask.id,
                    activeContainerId,
                    activeContainerId, // Same list
                    newPosition
                );


            } else {
                // Moving tasks between lists


                const activeList = myBoard.lists.find(list => list.id === activeContainerId);
                const overList = myBoard.lists.find(list => list.id === overContainerId);

                const overIndex = (() => {
                    if (over.id.startsWith('List-')) {
                        // Dropping over the list container, insert at the start(top 1)
                        return 0;
                    } else {
                        // Find the index of the overId task in the overList
                        const index = overList.tasks.findIndex(task => task?.id === overId);
                        if (index >= 0) {
                            // Determine whether to insert before or after the target task
                            const isBelowMiddle = active.rect.current.translated
                                ? active.rect.current.translated.top > over.rect.top + over.rect.height / 2
                                : active.rect.current.initial.top > over.rect.top + over.rect.height / 2;
                            return isBelowMiddle ? index + 1 : index;
                        } else {
                            // If overId is not found, insert at the end
                            return overList.tasks.length;
                        }
                    }
                })();


                if (overList.title === "Doing") {
                    // Update the state optimistically inside handleMarkAsDoing
                    await handleMarkAsDoing(activeId, overIndex, activeContainerId);
                    return;  // Exit the function since handleMarkAsDoing already updates the state
                } else if (overList.title === "Done") {
                    // Update the state optimistically inside handleMarkAsDone
                    await handleMarkAsDone(activeId, activeContainerId);
                    return;  // Exit the function since handleMarkAsDone already updates the state
                } else {
                    const activeTaskIndex = activeList.tasks.findIndex(task => task?.id === activeId);
                    if (activeTaskIndex === -1) return;

                    const [movedTask] = activeList.tasks.splice(activeTaskIndex, 1);
                    movedTask.list = overContainerId;

                    // Update task counts
                    activeList.task_count = activeList.tasks.length;

                    overList.tasks.splice(overIndex, 0, movedTask);
                    overList.task_count = overList.tasks.length;

                    // Update the state immediately
                    setMyBoard(prevBoard => ({
                        ...prevBoard,
                        lists: prevBoard.lists.map(list => {
                            if (list.id === activeContainerId) return { ...activeList };
                            if (list.id === overContainerId) return { ...overList };
                            return list;
                        })
                    }));

                    // Then make the API call
                    const response = await UpdateTaskDragging(
                        myWorkspaces[0].id,
                        myBoard.project,
                        movedTask.id,
                        activeContainerId,
                        overContainerId,
                        overIndex
                    );

                    // Optionally handle the response
                    if (response && response.data) {
                        // Update task data if needed
                        const updatedTask = response.data;

                        setMyBoard(prevBoard => {
                            const updatedLists = prevBoard.lists.map(list => {
                                if (list.id === overContainerId) {
                                    const updatedTasks = list.tasks.map(task =>
                                        task?.id === updatedTask.id ? { ...task, ...updatedTask } : task
                                    );
                                    return { ...list, tasks: updatedTasks };
                                }
                                return list;
                            });
                            return { ...prevBoard, lists: updatedLists };
                        });
                    }
                }
            }
        }
    };



    const findContainerId = (id) => {
        if (id.startsWith('List-')) return id.replace('List-', '');
        return myBoard.lists.find(list =>
            list.tasks.some(task => task?.id === id)
        )?.id;
    };

    const handleDragOver = ({ active, over }) => {
        if (!over || !myBoard) return;
        const activeContainerId = findContainerId(active.id);
        const overContainerId = findContainerId(over.id);

        if (activeContainerId && overContainerId && activeContainerId !== overContainerId) {
            setMyBoard(prevBoard => {
                const activeList = prevBoard.lists.find(list => list.id === activeContainerId);
                const overList = prevBoard.lists.find(list => list.id === overContainerId);
                if (!activeList || !overList) return prevBoard;

                const activeTaskIndex = activeList.tasks.findIndex(task => task?.id === active.id);
                if (activeTaskIndex === -1) return prevBoard;
                const activeTask = activeList.tasks[activeTaskIndex];

                const newActiveListTasks = [...activeList.tasks];
                newActiveListTasks.splice(activeTaskIndex, 1);

                const overIndex =
                    over.id.startsWith('List-')
                        ? 0
                        : overList.tasks.findIndex(task => task?.id === over.id);

                const newOverListTasks = [...overList.tasks];
                newOverListTasks.splice(overIndex, 0, activeTask);
                return {
                    ...prevBoard,
                    lists: prevBoard.lists.map(list => {
                        if (list.id === activeContainerId) return { ...activeList, tasks: newActiveListTasks };
                        if (list.id === overContainerId) return { ...overList, tasks: newOverListTasks };
                        return list;
                    })
                };
            });
        }
    };

    const renderDragOverlay = () => {
        if (activeId && myBoard) {
            const draggingTask = myBoard.lists
                .flatMap(list => list.tasks)
                .find(task => task?.id === activeId);
            if (draggingTask) {
                return (
                    <>
                        {(draggingTask.cover_image || draggingTask.cover_color) && (
                            <Paper
                                className={`list-cover-paper-project-page ${draggingTask.cover_image ? 'image' : ''}`}
                                sx={{
                                    backgroundColor: draggingTask.cover_color,
                                    backgroundImage: draggingTask.cover_image
                                        ? `url(${draggingTask.cover_image.startsWith('http') ? draggingTask.cover_image : `${process.env.REACT_APP_BACKEND_URL}${draggingTask.cover_image}`})`
                                        : 'none',
                                }}
                            />
                        )}
                        <Paper
                            elevation={4}
                            className={`task-paper-project-page dragging-task-paper-project-page ${draggingTask.cover_image || draggingTask.cover_color ? 'cover' : ''}`}
                            sx={{
                                borderRadius: '8px',
                                padding: '16px',
                                mt: 1
                            }}
                        >
                            <div className="flex-project-page">
                                {myBoard && draggingTask?.label?.map((labelId, index) => {
                                    // Find the full label object by matching the labelId with myBoard.labels
                                    const fullLabel = myBoard.labels.find(label => label.id === labelId);

                                    // If the full label is found, render it
                                    if (fullLabel) {
                                        return (
                                            <div
                                                key={index}
                                                className={`label-style-project-page ${fullLabel.color === "rainbow" ? 'rainbow' : ''}`}
                                                style={{ backgroundColor: fullLabel.color }}
                                            >
                                                <Typography
                                                    className={`label-title-project-page ${fullLabel.color === "rainbow" ? 'no-shadow' : 'with-shadow'}`}
                                                    sx={{ color: getTextColor(fullLabel.color) }}
                                                >
                                                    {fullLabel.title}
                                                </Typography>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>


                            <div className="dragging-task-title-project-page">
                                <b>#{draggingTask.frontend_order}</b> - {draggingTask.title}
                            </div>

                            <div className="dragging-task-dates-project-page">
                                {(draggingTask.start_date || draggingTask.due_date || draggingTask.end_date) && (
                                    <ScrollAwareTooltip title={getColorForTask(draggingTask).tooltip} arrow>
                                        <div className="dragging-task-tooltip-project-page">
                                            <div className="dragging-task-tooltip-content-project-page"
                                                style={{
                                                    backgroundColor: getColorForTask(draggingTask).backgroundColor,
                                                    color: getColorForTask(draggingTask).color,
                                                }}
                                            >
                                                <AccessTimeIcon className="access-time-icon-project-page" />
                                                {draggingTask.end_date ? formatDate(draggingTask.end_date) : draggingTask.due_date ? formatDate(draggingTask.due_date) : draggingTask.start_date ? formatDate(draggingTask.start_date) : ''}
                                            </div>
                                        </div>
                                    </ScrollAwareTooltip>
                                )}
                                {draggingTask.has_description && (
                                    <SubjectIcon />
                                )}
                                {draggingTask.has_attachments && (
                                    <AttachFileIcon />
                                )}
                                {draggingTask.checklist_completion && draggingTask.checklist_completion.total > 0 && (
                                    <ScrollAwareTooltip title={t('project_page.checklist')} arrow>
                                        <div className="checkbox-div-project-page">
                                            <CheckBoxIcon className="checkbox-icon-project-page" />
                                            <Typography variant="body2" className="checkbox-text-project-page">
                                                {`${draggingTask.checklist_completion.completed}/${draggingTask.checklist_completion.total}`}
                                            </Typography>
                                        </div>
                                    </ScrollAwareTooltip>
                                )}
                                <div className="members-avatar-div-project-page">
                                    <AvatarGroup
                                        max={4}
                                        className="members-avatar-group-project-page"
                                    >
                                        {draggingTask.members?.map((memberId, index) => {
                                            // Find the full member object in workspaceWorkers by matching the member ID
                                            const fullMember = workspaceWorkers?.find(worker => worker.id === memberId);

                                            // If the full member is found, render the Avatar with member's info
                                            if (fullMember) {
                                                return (
                                                    <ScrollAwareTooltip title={fullMember.full_name || fullMember.username} arrow key={fullMember.id}>
                                                        <Avatar
                                                            className="members-avatar-project-page"
                                                            src={`${fullMember.image}`}
                                                        />
                                                    </ScrollAwareTooltip>
                                                );
                                            }
                                            return null; // Return null if the member is not found (optional)
                                        })}
                                    </AvatarGroup>
                                </div>

                            </div>
                        </Paper>
                    </>
                );
            }
        }
        return null;
    };

    const handleTaskRightClick = (event, task, list) => {
        event.preventDefault();
        event.stopPropagation();
        setContextTaskMenuPosition({ x: event.clientX, y: event.clientY });
        setSelectedTask(task);
        setSelectedList(list);
    };

    const handleTaskCloseMenu = () => {
        setContextTaskMenuPosition({ x: 0, y: 0 });
        setViewTaskModalOpen(false);
    };

    const handleEditTask = () => {
        setEditTaskMode(selectedTask.id);
        handleTaskCloseMenu();
    };

    const handleContextMenu = (event) => {
        event.preventDefault();
    };

    const handleTaskTitleChange = (event) => {
        setEditTaskNewTitle(event.target.value)
    };

    const handleListTitleChange = (event) => {
        const newValue = event.target.value.toLowerCase();
        if (newValue === "to do" || newValue === "doing" || newValue === "done") {
            setTitleError(true);
        } else {
            setTitleError(false);
            setEditListNewTitle(event.target.value);
        }
    };

    const handleListRightClick = (event, list) => {
        setContextListMenuPosition({ x: event.clientX, y: event.clientY });
        setSelectedList(list);
    };

    const OpenContextMenu = (event, list) => {
        event.preventDefault(); // Prevent default context menu from appearing
        event.stopPropagation();
        handleListRightClick(event, list); // Call your function to open the custom context menu
    };

    const OpenContextMenuTask = (event, task, list) => {
        event.preventDefault(); // Prevent default context menu from appearing
        event.stopPropagation();
        handleTaskRightClick(event, task, list); // Call your function to open the custom context menu
    };

    const handleListCloseMenu = () => {
        setContextListMenuPosition({ x: 0, y: 0 });
    };

    const handleEditList = () => {
        setEditListMode(selectedList.id);
        setEditListNewTitle(selectedList.title);
        handleListCloseMenu();
    };

    const handleArchiveListModalClose = () => {
        setOpenArchiveListModal(false);
        handleListCloseMenu();
    };

    const handleSaveEditTask = async () => {
        if (editTaskNewTitle !== null) {

            await UpdateTask(myWorkspaces[0].id, myBoard.project, selectedList, selectedTask, editTaskNewTitle);
            const updatedBoard = { ...myBoard };
            const listToUpdate = updatedBoard.lists.find(list => list.id === selectedList.id);
            const taskToUpdate = listToUpdate.tasks.find(task => task?.id === selectedTask.id);
            if (taskToUpdate) {
                taskToUpdate.title = editTaskNewTitle;
                setMyBoard(updatedBoard);
            }
        }
        setEditTaskMode(null);
    };

    const handleArchiveTask = async () => {
        ArchiveTask(myWorkspaces[0].id, myBoard.project, selectedList, selectedTask);
        const updatedBoard = { ...myBoard };
        updatedBoard.lists.forEach(list => {
            list.tasks = list.tasks.filter(task => task?.id !== selectedTask.id);
            if (selectedList.id === list.id) {
                list.task_count = list.task_count - 1;
            }
        });
        setMyBoard(updatedBoard);
        handleArchiveTaskModalClose();
    };

    const handleSaveEditList = async () => {
        if (editListNewTitle !== null) {
            UpdateList(myWorkspaces[0].id, myBoard.project, selectedList, editListNewTitle, myBoard.id);
            const updatedBoard = { ...myBoard };
            const listToUpdate = updatedBoard.lists.find(list => list.id === selectedList.id);
            if (listToUpdate) {
                listToUpdate.title = editListNewTitle;
                setMyBoard(updatedBoard);
            }
        }
        setEditListMode(null);
    };

    const handleArchiveList = async () => {
        ArchiveList(myWorkspaces[0].id, myBoard.project, selectedList)
        const updatedBoard = { ...myBoard };
        updatedBoard.lists = updatedBoard.lists.filter(list => list.id !== selectedList.id);
        setMyBoard(updatedBoard);
        handleArchiveListModalClose();
    };


    const handleViewTask = (task, list) => {
        if (!task) return;
        openViewTaskModalHandler(task, list);
        setModalSelectedTask(task);
        handleListCloseMenu();
    };

    const updateToggleLabel = () => {
        ToggleFullLabel(myWorkspaces[0].id, myBoard.project, myBoard.id);
        setMyBoard(prevState => ({
            ...prevState,
            full_label: !prevState.full_label
        }));
    };

    const handleMarkAsDoing = async (taskId, targetIndex = 0, sourceListId) => {
        handleTaskCloseMenu();

        if (!myUserInMemberFormat) return;

        const newBoard = { ...myBoard };
        let taskToMove = null;
        let doingList = null;

        // Find and remove the task from its current list
        newBoard.lists.forEach((list) => {
            if (list.id === sourceListId) {
                const taskIndex = list.tasks.findIndex((task) => task?.id === taskId);
                if (taskIndex !== -1) {
                    taskToMove = list.tasks.splice(taskIndex, 1)[0];
                    list.task_count--;
                }
            }
        });

        if (!taskToMove) {
            console.error("Task not found in the source list.");
            return;
        }

        doingList = newBoard.lists.find((list) => list.title === "Doing");
        if (!doingList) {
            console.error('"Doing" list not found.');
            return;
        }

        taskToMove.list = doingList.id;
        taskToMove.start_date = new Date().toISOString();

        if (!taskToMove.members) {
            taskToMove.members = [];
        }

        if (!taskToMove.members.includes(myUserInMemberFormat.id)) {
            taskToMove.members.push(myUserInMemberFormat.id);
        }

        doingList.tasks.splice(targetIndex, 0, taskToMove);
        doingList.task_count = doingList.tasks.length;

        setMyBoard(newBoard);

        const response = await UpdateTaskDragging(
            myWorkspaces[0].id,
            myBoard.project,
            taskId,
            sourceListId,
            doingList.id,
            targetIndex
        );

        if (response && response.data) {
            const updatedTask = response.data;

            setMyBoard(prevBoard => {
                const updatedLists = prevBoard.lists.map(list => {
                    if (list.id === doingList.id) {
                        const updatedTasks = list.tasks.map(task =>
                            task?.id === updatedTask.id ? { ...task, ...updatedTask } : task
                        );
                        return { ...list, tasks: updatedTasks };
                    }
                    return list;
                });
                return { ...prevBoard, lists: updatedLists };
            });
        }
    };

    const handleMarkAsDone = async (taskId, sourceListId) => {
        handleTaskCloseMenu();
        if (!myUserInMemberFormat) return;

        const newBoard = { ...myBoard };
        let taskToMove = null;
        let doneList = null;

        newBoard.lists.forEach((list) => {
            if (list.id === sourceListId) {
                const taskIndex = list.tasks.findIndex((task) => task?.id === taskId);
                if (taskIndex !== -1) {
                    taskToMove = list.tasks.splice(taskIndex, 1)[0];
                    list.task_count--;
                }
            }
        });

        if (!taskToMove) {
            console.error("Task not found in the source list.");
            return;
        }

        doneList = newBoard.lists.find((list) => list.title === "Done");
        if (!doneList) {
            console.error('"Done" list not found.');
            return;
        }

        taskToMove.list = doneList.id;

        taskToMove.list = doneList.id;
        taskToMove.end_date = new Date().toISOString();

        if (!taskToMove.members) {
            taskToMove.members = [];
        }

        if (!taskToMove.members.includes(myUserInMemberFormat.id)) {
            taskToMove.members.push(myUserInMemberFormat.id);
        }

        doneList.tasks.unshift(taskToMove);
        doneList.task_count = doneList.tasks.length;

        setMyBoard(newBoard);

        const response = await UpdateTaskDragging(
            myWorkspaces[0].id,
            myBoard.project,
            taskId,
            sourceListId,
            doneList.id,
            0
        );

        if (response && response.data) {
            const updatedTask = response.data;

            setMyBoard(prevBoard => {
                const updatedLists = prevBoard.lists.map(list => {
                    if (list.id === doneList.id) {
                        const updatedTasks = list.tasks.map(task =>
                            task?.id === updatedTask.id ? { ...task, ...updatedTask } : task
                        );
                        return { ...list, tasks: updatedTasks };
                    }
                    return list;
                });
                return { ...prevBoard, lists: updatedLists };
            });
        }
    };

    const getColorForTask = (task) => {
        if (!myUserTimezone) {
            return "transparent";
        }
        const startDate = task.start_date
        const dueDate = task.due_date
        const endDate = task.end_date

        if (dueDate && endDate && new Date(endDate) > new Date(dueDate)) {
            const timeDifference = new Date(endDate) - new Date(dueDate);

            const overdueDays = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
            const key = overdueDays === 1 ? 'project_page.due_completed_singular' : 'project_page.due_completed_plural';

            return {
                backgroundColor: "#5d1f1a",
                color: "#fd9891",
                tooltip: t(key, { count: overdueDays })
            };
        }
        if (endDate) {
            return { backgroundColor: "#4bce97", color: "#1D2125", tooltip: t('project_page.completed_task', { date: formatDate(endDate) }) };
        }
        if (dueDate) {
            return { backgroundColor: "#f5cd47", color: "#1D2125", tooltip: t('project_page.due_by', { date: formatDate(dueDate) }) };
        }
        if (startDate) {
            return { backgroundColor: "#1a325d", color: "#91c6fd", tooltip: t('project_page.start_task', { date: formatDate(startDate) }) };
        }
        return { backgroundColor: "transparent", color: "#000000", tooltip: "" };
    };

    const formatDate = (date) => {
        const monthNames = [
            t('project_page.months.jan'),
            t('project_page.months.feb'),
            t('project_page.months.mar'),
            t('project_page.months.apr'),
            t('project_page.months.may'),
            t('project_page.months.jun'),
            t('project_page.months.jul'),
            t('project_page.months.aug'),
            t('project_page.months.sep'),
            t('project_page.months.oct'),
            t('project_page.months.nov'),
            t('project_page.months.dec')
        ];


        const newDate = new Date(date);
        const day = newDate.getDate();
        const month = monthNames[newDate.getMonth()];

        return `${day} ${month}`;
    };

    const handleSendToTop = async (contextTask, contextList) => {
        if (contextTask) {
            const listId = contextList.id;
            const list = myBoard.lists.find(list => list.id === listId);
            if (!list) {
                console.error("List not found.");
                return;
            }

            const taskIndex = list.tasks.findIndex(task => task?.id === contextTask.id);
            if (taskIndex === -1) {
                console.error("Task not found in the list.");
                return;
            }

            const newPosition = 0;

            if (taskIndex === newPosition) {
                return;
            }

            const updatedTasks = moveItem(list.tasks, taskIndex, newPosition);

            setMyBoard(prevBoard => ({
                ...prevBoard,
                lists: prevBoard.lists.map(l =>
                    l.id === listId ? { ...l, tasks: updatedTasks } : l
                )
            }));

            handleTaskCloseMenu();

            const response = await UpdateTaskDragging(
                myWorkspaces[0].id,
                myBoard.project,
                contextTask.id,
                listId,
                listId, // Same list
                newPosition
            );

            if (response && response.updated_task) {
                const updatedTask = response.updated_task;

                setMyBoard(prevBoard => ({
                    ...prevBoard,
                    lists: prevBoard.lists.map(l => {
                        if (l.id === listId) {
                            const updatedTasks = l.tasks.map(task =>
                                task?.id === updatedTask.id ? { ...task, ...updatedTask } : task
                            );
                            return { ...l, tasks: updatedTasks };
                        }
                        return l;
                    })
                }));
            }
        }
    };

    /*
    const handleSendToBottom = async (contextTask, contextList) => {
        if (contextTask) {
            const listId = contextList.id;
            const list = myBoard.lists.find(list => list.id === listId);
            if (!list) {
                console.error("List not found.");
                return;
            }
    
            const taskIndex = list.tasks.findIndex(task => task?.id === contextTask.id);
            if (taskIndex === -1) {
                console.error("Task not found in the list.");
                return;
            }
    
            const newPosition = list.tasks.length - 1;
    
            if (taskIndex === newPosition) {
                return;
            }
    
            const updatedTasks = moveItem(list.tasks, taskIndex, new); 
    
            setMyBoard(prevBoard => ({
                ...prevBoard,
                lists: prevBoard.lists.map(l =>
                    l.id === listId ? { ...l, tasks: updatedTasks } : l
                )
            }));
    
            handleTaskCloseMenu();
    
            const response = await UpdateTaskDragging(
                myWorkspaces[0].id,
                myBoard.project,
                contextTask.id,
                listId,
                listId, 
                newPosition
            );
    
            if (response && response.updated_task) {
                const updatedTask = response.updated_task;
    
                setMyBoard(prevBoard => ({
                    ...prevBoard,
                    lists: prevBoard.lists.map(l => {
                        if (l.id === listId) {
                            const updatedTasks = l.tasks.map(task =>
                                task?.id === updatedTask.id ? { ...task, ...updatedTask } : task
                            );
                            return { ...l, tasks: updatedTasks };
                        }
                        return l;
                    })
                }));
            }
        }
    };*/
    
    const [, setPreviousFullLabel] = useState(null);

    useEffect(() => {
        if (myBoard) {
            setPreviousFullLabel(myBoard.full_label);
        }
    }, [myBoard]);

    const handleToggleListCollapse = (listId) => {
        if (collapsedLists[listId]) {
            setCollapsedLists(prevState => ({
                ...prevState,
                [listId]: false
            }));
        } else {
            setCollapsing(prevState => ({
                ...prevState,
                [listId]: true
            }));

            setTimeout(() => {
                setCollapsedLists(prevState => ({
                    ...prevState,
                    [listId]: true
                }));
                setCollapsing(prevState => ({
                    ...prevState,
                    [listId]: false
                }));
            }, transitionDuration);
        }
        handleListCloseMenu();
        UpdateListCollapsable(myWorkspaces[0].id, myBoard.project, listId);
    };

    const getCardContentStyle = (isDraggingOver, isCollapsed, isCollapsing) => {
        return `
        card-content-project-page
         ${isCollapsed || isCollapsing ? 'collapsed' : ''}
         ${isDraggingOver ? 'isdragging' : ''}
        `;
    };

    const getOverArchingBoxStyle = (isCollapsed, isCollapsing) => {
        return `
        over-arching-box-project-page
         ${isCollapsed || isCollapsing ? 'collapsed' : ''}
        `;
    };

    const getBoxStyle = (isCollapsed, isCollapsing) => {
        return `
        box-style-project-page
        ${isCollapsed || isCollapsing ? 'collapsed' : ''}
        `;
    };

    const transitionDuration = 1250;

    const colorOptions = [
        '#7ee2b8',
        '#f5cd47',
        '#fea362',
        '#f87168',
        '#9f8fef',
        '#579dff',
        '#6cc3e0',
        '#94c748',
        '#e774bb',
        '#252224'
    ];

    const getFontColor = (list_color) => {
        switch (list_color) {
            case '#7ee2b8':
                return '#174B35';
            case '#f5cd47':
                return '#533F03';
            case '#fea362':
                return '#5A2C06';
            case '#f87168':
                return '#5D1F1A';
            case '#9f8fef':
                return '#2D235D';
            case '#579dff':
                return '#1A325D';
            case '#6cc3e0':
                return '#1A3A47';
            case '#94c748':
                return '#2F4514';
            case '#e774bb':
                return '#5D1F45';
            case '#252224':
                return '#C0C0C0';
            default:
                return '#000000'; // Default font color if none match
        }
    }

    const handleColorChange = (color) => {
        updateListColor(selectedList.id, color);
    };

    const updateListColor = async (listId, color) => {
        setMyBoard((prevBoard) => {
            const updatedLists = prevBoard.lists.map((list) => {
                if (list.id === listId) {
                    return { ...list, color };
                }
                return list;
            });
            return { ...prevBoard, lists: updatedLists };
        });
        await UpdateListColor(myWorkspaces[0].id, myBoard.project, listId, color);
    };

    const key = selectedList.task_count === 1 ? 'project_page.task_count_message_singular' : 'project_page.task_count_message_plural';

    const customCollisionDetection = (args) => {
        const { active, droppableContainers } = args;
        if (active.id.startsWith('List-')) {
            const listContainers = droppableContainers.filter(container => container.id.startsWith('List-'));
            return closestCenter({ ...args, droppableContainers: listContainers });
        }
        return closestCorners(args);
    };

    const taskMenuItems = selectedTask && selectedTask.id ? [
        <MenuItem onClick={() => openViewTaskModalHandler(selectedTask, selectedList)} key="task-title">
            <Box className="menu-item-task-project-page" sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ pb: 0 }}>{selectedTask ? selectedTask.title : t('project_page.no_task')}</Typography>
                <Typography variant='subtitle2' color='#67B5D9' className="task-actions-project-page-task-sub" >
                    <OpenInNewIcon fontSize='small' sx={{ color: '#67B5D9' }} className="task-actions-project-page" />
                    {t('project_page.open_task')}
                </Typography>
            </Box>
        </MenuItem>,
        <Divider className="divider-project-page" key="divider" />,
        <MenuItem onClick={() => handleMarkAsDoing(selectedTask.id, 0, selectedList.id)} key="mark-as-doing">
            <PlayCircleOutlineIcon className="task-actions-project-page" />
            {t('project_page.mark_doing')}
        </MenuItem>,
        <MenuItem onClick={() => handleMarkAsDone(selectedTask.id, selectedList.id)} key="mark-as-done">
            <CheckCircleOutlineIcon className="task-actions-project-page" />
            {t('project_page.mark_done')}
        </MenuItem>,
        <MenuItem onClick={() => handleEditTask()} key="edit-task">
            <EditIcon className="task-actions-project-page" />
            {t('project_page.edit_task')}
        </MenuItem>,
        <MenuItem onClick={() => { openViewTaskModalHandler(selectedTask, selectedList, true); }} key="edit-labels">
            <LabelImportantIcon className="task-actions-project-page" />
            {t('project_page.edit_labels')}
        </MenuItem>,
        <MenuItem onClick={() => { openViewTaskModalHandler(selectedTask, selectedList, false, true); }} key="edit-cover">
            <WebAssetIcon className="task-actions-project-page" />
            {t('project_page.edit_cover')}
        </MenuItem>,
        <MenuItem onClick={() => handleArchiveTaskModalOpen()} key="archive-task">
            <ArchiveIcon className="task-actions-project-page" />
            {t('project_page.archive_task')}
        </MenuItem>,
    ] : [];

    if (selectedList?.tasks?.length > 0 && selectedList.tasks[0].id !== selectedTask?.id) {
        taskMenuItems.push(
            <MenuItem onClick={() => handleSendToTop(selectedTask, selectedList)} key="send-to-top">
                <VerticalAlignTopIcon className="task-actions-project-page" />
                {t('project_page.send_top')}
            </MenuItem>
        );
    }

    /*
    if (selectedList?.tasks?.length > 0 && selectedList.tasks[selectedList.task_count-1]?.id !== selectedTask?.id) {
        taskMenuItems.push(
            <MenuItem onClick={() => handleSendToBottom(selectedTask, selectedList)} key="send-to-bottom">
                <VerticalAlignBottomIcon className="task-actions-project-page" />
                {t('project_page.send_bottom')}
            </MenuItem>
        );
    }*/

    const listMenuItems = [];

    listMenuItems.push(
        <MenuItem disableRipple key="list-title">
            <Box className="menu-item-task-project-page menu-item-list-project-page">
                {selectedList ? selectedList.title : t('global.no_list')}
            </Box>
        </MenuItem>,
        <Divider className="divider-project-page" key="divider" />,
        <MenuItem key="header" onClick={() => handleListHeader()}>{t('project_page.edit_header')}</MenuItem>
    );

    // Conditionally add toggleCollapse
    if (editListMode !== selectedList.id) {
        listMenuItems.push(
            <MenuItem key="toggleCollapse" onClick={() => handleToggleListCollapse(selectedList.id)}>
                {collapsedLists[selectedList.id] ? t('project_page.expand') : t('project_page.collapse')}
            </MenuItem>
        );
    }

    // Conditionally add editList and archiveList
    if (!(selectedList.title === "Doing" || selectedList.title === "To Do" || selectedList.title === "Done")) {
        listMenuItems.push(
            <MenuItem key="editList" onClick={() => handleEditList()}>{t('project_page.edit_title')}</MenuItem>,
            <MenuItem key="archiveList" onClick={() => handleArchiveListModalOpen()}>{t('project_page.archive_list')}</MenuItem>
        );
    }

    listMenuItems.push(
        <MenuItem
            key="toggleTaskCount"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToggleTaskCount(selectedList.id, !selectedList.show_total_tasks);
            }}
        >
            <Checkbox
                checked={selectedList.show_total_tasks}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleToggleTaskCount(selectedList.id, !selectedList.show_total_tasks);
                }}
                className='white-txt-color check-total-toggle'

            />
            <Typography sx={{ display: 'flex' }}> {t('project_page.toggle_tasks')}</Typography>
        </MenuItem>
    );

    // Color change menu item
    listMenuItems.push(
        <MenuItem key="changeColor" onClick={() => setShowColorOptions(!showColorOptions)}>
            {t('project_page.change_color')}
            {showColorOptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </MenuItem>
    );

    if (showColorOptions) {
        listMenuItems.push(
            <Box className="color-options-project-page" key="color-options">
                {colorOptions.map((color) => (
                    <Box
                        key={color}
                        onClick={() => handleColorChange(color)}
                        className="show-colors-project-page"
                        sx={{ backgroundColor: color }}
                    />
                ))}
            </Box>
        );
    }

    return (
        <>
            {isLoading ? (
                <BoardSkeleton />
            ) : (
                <>
                    {/* Dark Overlay */}
                    {shouldRenderOverlay && (
                        <div
                            className={`dark-overlay-project-page ${isOverlayVisible ? 'visible' : ''}`}
                        />
                    )}
                    <div ref={containerRef} className="container-div-project-page">
                        <div className="flex-div-project-page">
                            <div className="my-lists-project-page">
                                <DndContext sensors={sensors} collisionDetection={customCollisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                                    {myBoard?.lists && (
                                        <>
                                            <SortableContext items={myBoard?.lists?.map((list) => `List-${list.id}`)} strategy={horizontalListSortingStrategy}>
                                                {myBoard?.lists?.map((list, index) => (
                                                    <SortableItem
                                                        key={`List-${list.id}`}
                                                        id={`List-${list.id}`}
                                                        index={index}
                                                    >

                                                        <Box
                                                            className={getOverArchingBoxStyle(collapsedLists[list.id], collapsing[list.id])}
                                                            style={{
                                                                transition: `width ${transitionDuration}ms ease-in-out`,
                                                                backgroundColor: list.color,
                                                            }}
                                                        >
                                                            <SortableContext
                                                                items={list.tasks.filter(task => task).map(task => task.id)}
                                                                strategy={verticalListSortingStrategy}
                                                            >
                                                                <CardContent
                                                                    onContextMenu={(event) => handleListRightClick(event, list)}
                                                                    className={getCardContentStyle(false, collapsedLists[list.id], collapsing[list.id])}
                                                                    style={{
                                                                        transition: `max-height ${transitionDuration}ms ease-in-out, padding ${transitionDuration}ms ease-in-out`
                                                                    }}
                                                                >
                                                                    <Typography variant="h5" gutterBottom className="edit-list-project-page">
                                                                        {editListMode === list.id ? (
                                                                            <Box key={`edit-${list.id}`} className="edit-list-container">
                                                                                <Box className="edit-list-box-project-page">
                                                                                    <TextField

                                                                                        id="standard-multiline-flexible"
                                                                                        multiline
                                                                                        maxRows={4}
                                                                                        variant="standard"
                                                                                        value={editListNewTitle}
                                                                                        onChange={handleListTitleChange}
                                                                                        error={titleError}
                                                                                        helperText={titleError && t('project_page.list_title_err')}
                                                                                        InputProps={{
                                                                                            className: "label-title-input-project-page",
                                                                                            style: {
                                                                                                color: getFontColor(list.color)
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                    <Box className="edit-list-button-project-page">
                                                                                        <Button
                                                                                            variant="text"
                                                                                            className="button-dark-bg"
                                                                                            disabled={titleError}
                                                                                            onClick={() => handleSaveEditList()}
                                                                                        >
                                                                                            {t('global.save_changes')}
                                                                                            <EditIcon className="edit-icon-project-page" />
                                                                                        </Button>
                                                                                    </Box>
                                                                                </Box>
                                                                            </Box>
                                                                        ) : collapsedLists[list.id] ? (
                                                                            <Box key={`collapsed-${list.id}`}>
                                                                                <ScrollAwareTooltip title={t('project_page.list_collapsed', { title: list.title })} arrow>
                                                                                    <ListIcon
                                                                                        className="list-icon-project-page"
                                                                                        style={{ color: getFontColor(list.color) }} />
                                                                                </ScrollAwareTooltip>
                                                                                <div className="collapsed-list-div-project-page">
                                                                                    <div className="collapsed-list-div-project-page collapsed-list-gap-project-page">
                                                                                        <span
                                                                                            className="collapsed-list-span-project-page"
                                                                                            style={{
                                                                                                color: getFontColor(list.color)
                                                                                            }}>
                                                                                            {list.title}
                                                                                        </span>
                                                                                        <ScrollAwareTooltip
                                                                                            title={t(key, { count: list.task_count })}
                                                                                            arrow
                                                                                        >
                                                                                            <Badge
                                                                                                badgeContent={list.task_count}
                                                                                                showZero
                                                                                                max={999}
                                                                                                className="badge-rotated-project-page"
                                                                                                sx={{
                                                                                                    '& .MuiBadge-badge': {
                                                                                                        backgroundColor: getFontColor(list.color),
                                                                                                        color: list.color === '#252224' ? '#000000' : undefined,
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        </ScrollAwareTooltip>
                                                                                        {(list.title === "Done" || list.title === "Doing" || list.title === "To Do") && (
                                                                                            <ScrollAwareTooltip title={t('project_page.list_important')}>
                                                                                                <LockIcon
                                                                                                    className="lock-icon-rotated-project-page"
                                                                                                    sx={{
                                                                                                        color: getFontColor(list.color),
                                                                                                    }}
                                                                                                />
                                                                                            </ScrollAwareTooltip>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </Box>
                                                                        ) : (
                                                                            <Box key={`expanded-${list.id}`} className="list-header-box-project-page">
                                                                                <Box className="list-header-project-page">
                                                                                    <ScrollAwareTooltip title={list.title}>
                                                                                        <Typography
                                                                                            className="typography-title-project-page"
                                                                                            sx={{
                                                                                                color: getFontColor(list.color)
                                                                                            }}
                                                                                        >
                                                                                            {list.title}
                                                                                        </Typography>
                                                                                    </ScrollAwareTooltip>
                                                                                </Box>
                                                                                <Box
                                                                                    className="checkbox-div-project-page"
                                                                                >
                                                                                    <IconButton
                                                                                        onClick={(event) => handleMenuOpen(event, list)}
                                                                                        className={`filter-icon-project-page ${list.show_total_tasks ? 'show_tasks' : ''}`}
                                                                                        sx={{ color: getFontColor(list.color) }}
                                                                                    >
                                                                                        <FilterListIcon />
                                                                                    </IconButton>


                                                                                    {list.show_total_tasks && (
                                                                                        <Tooltip
                                                                                            title={t(key, { count: list.task_count })}
                                                                                            arrow
                                                                                        >
                                                                                            <Badge
                                                                                                badgeContent={list.task_count}
                                                                                                showZero
                                                                                                max={999}
                                                                                                className={`badge-project-page ${(list.title === "Done" || list.title === "Doing" || list.title === "To Do") ? 'locked' : ''}`}
                                                                                                sx={{
                                                                                                    '& .MuiBadge-badge': {
                                                                                                        backgroundColor: getFontColor(list.color),
                                                                                                        color: list.color === '#252224' ? '#000000' : undefined,
                                                                                                    },
                                                                                                }}
                                                                                            />
                                                                                        </Tooltip>
                                                                                    )}
                                                                                    {(list.title === "Done" || list.title === "Doing" || list.title === "To Do") && (
                                                                                        <Tooltip title={t('project_page.list_important')}>
                                                                                            <LockIcon
                                                                                                className="lock-icon-project-page"
                                                                                                sx={{
                                                                                                    color: getFontColor(list.color)
                                                                                                }}
                                                                                            />
                                                                                        </Tooltip>
                                                                                    )}
                                                                                    <MoreVertIcon className="options-icon-list-project-page" sx={{ color: getFontColor(list.color) }} onClick={(event) => OpenContextMenu(event, list)} />
                                                                                </Box>
                                                                            </Box>
                                                                        )}
                                                                    </Typography>
                                                                    {/* HEADER IS HERE!! */}
                                                                    <Box
                                                                        ref={(el) => {
                                                                            if (el) {
                                                                                // Assign the DOM element to the ref
                                                                                listRefs.current[list.id] = el;

                                                                                // Check if a scroll handler already exists for this list
                                                                                if (!scrollHandlersRef.current[list.id]) {
                                                                                    // Define the scroll handler
                                                                                    const handleScrollForList = () => handleScroll(list.id);

                                                                                    // Store the handler so it can be removed later
                                                                                    scrollHandlersRef.current[list.id] = handleScrollForList;

                                                                                    // Add the scroll event listener
                                                                                    el.addEventListener('scroll', handleScrollForList);
                                                                                }
                                                                            } else {
                                                                                // Element is being unmounted, remove the scroll event listener
                                                                                if (listRefs.current[list.id] && scrollHandlersRef.current[list.id]) {
                                                                                    listRefs.current[list.id].removeEventListener('scroll', scrollHandlersRef.current[list.id]);
                                                                                    delete scrollHandlersRef.current[list.id];
                                                                                }

                                                                                // Remove the reference to the DOM element
                                                                                delete listRefs.current[list.id];
                                                                            }
                                                                        }}
                                                                        id={`list-${list.id}`}
                                                                        className={getBoxStyle(collapsedLists[list.id], collapsing[list.id])}
                                                                        style={{ transition: `width ${transitionDuration}ms ease-in-out` }}
                                                                    >
                                                                        {!collapsedLists[list.id] && list.header && (list.header.title !== "" || list.header.image) && (
                                                                            <Paper
                                                                                className="list-header-paper-project-page"
                                                                                elevation={4}
                                                                                sx={{
                                                                                    backgroundImage: list.header.image ? `url(${list.header.image})` : 'none',
                                                                                    backgroundColor: list.header.color || '#D6D4D4',
                                                                                    color: list.header.title_color || '#000000',
                                                                                }}
                                                                            >
                                                                                {list.header.emoji && (
                                                                                    <Box
                                                                                        className="list-header-emoji-project-page"
                                                                                    >
                                                                                        {list.header.emoji}
                                                                                    </Box>
                                                                                )}
                                                                                {list.header.title && (
                                                                                    <Typography variant="h6" sx={{ color: list.header.title_color || '#333' }}>
                                                                                        {list.header.title}
                                                                                    </Typography>
                                                                                )}
                                                                            </Paper>
                                                                        )}
                                                                        {!collapsedLists[list.id] && list?.tasks?.map((task, index) => {
                                                                            const taskId = task ? task?.id : `placeholder-${list.id}-${index}`;
                                                                            return task ? (
                                                                                <div key={taskId} className={`collapsed-list-map-project-page ${isOverlayVisible && highlightedTaskId === taskId ? 'highlighted' : ''}`}>
                                                                                    <SortableItem key={taskId}
                                                                                        id={`${taskId}`}
                                                                                        index={index}
                                                                                        className={highlightedTaskId === taskId ? 'highlight-task-project-page' : ''}>
                                                                                        {
                                                                                            task.cover_image || task.cover_color ? (

                                                                                                <Paper
                                                                                                    className={`list-cover-paper-project-page ${task.cover_image ? 'image' : ''}`}
                                                                                                    sx={{
                                                                                                        backgroundColor: task.cover_color,
                                                                                                        backgroundImage: task.cover_image ? `url(${task.cover_image})` : 'none',
                                                                                                    }}
                                                                                                    onClick={editTaskMode === null ? () => handleViewTask(task, list) : undefined}
                                                                                                    onContextMenu={(event) => handleTaskRightClick(event, task, list)}
                                                                                                />
                                                                                            ) : null
                                                                                        }
                                                                                        < Paper
                                                                                            elevation={4}
                                                                                            className={`task-paper-project-page dragging-task-paper-project-page ${task.cover_image || task.cover_color ? 'cover' : ''}`}
                                                                                            onClick={editTaskMode === null ? () => handleViewTask(task, list) : undefined}
                                                                                            onContextMenu={(event) => handleTaskRightClick(event, task, list)}
                                                                                        >
                                                                                            <div className="flex-project-page">
                                                                                                {myBoard && task?.label?.map((labelId, index) => {
                                                                                                    // Find the full label object by matching the labelId with myBoard.labels
                                                                                                    const fullLabel = myBoard.labels.find(label => label.id === labelId);
                                                                                                    if (!fullLabel) return null;

                                                                                                    const textColor = getTextColor(fullLabel.color);

                                                                                                    return (
                                                                                                        <div
                                                                                                            key={`label-${labelId}`}
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                updateToggleLabel();
                                                                                                            }}
                                                                                                            className={`label-style-project-page
                                                                                                        ${myBoard.full_label ? 'label-style-project-page-with-text' : ''}
                                                                                                        ${fullLabel.color === 'rainbow' ? 'rainbow' : ''}`}
                                                                                                            style={{ backgroundColor: fullLabel.color }}
                                                                                                        >
                                                                                                            <Typography
                                                                                                                className={`label-title-project-page
                                                                                                            ${fullLabel.color === 'rainbow' ? 'no-shadow' : 'with-shadow'}`}
                                                                                                                style={{ color: textColor }}
                                                                                                            >
                                                                                                                {myBoard.full_label && fullLabel.title}
                                                                                                            </Typography>
                                                                                                        </div>
                                                                                                    );
                                                                                                })}
                                                                                            </div>


                                                                                            {editTaskMode === task?.id ? (
                                                                                                <>
                                                                                                    <TextField

                                                                                                        className="task"
                                                                                                        id="standard-multiline-flexible"
                                                                                                        multiline
                                                                                                        maxRows={4}
                                                                                                        variant="standard"
                                                                                                        defaultValue={task.title}
                                                                                                        onChange={(event) => handleTaskTitleChange(event, task?.id)}
                                                                                                    />
                                                                                                    <Button variant="text" onClick={() => handleSaveEditTask(task.description)}>
                                                                                                        {t('global.save_changes')}
                                                                                                        <EditIcon className="edit-icon-project-page" />
                                                                                                    </Button>
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    <div className="task-title-project-page">
                                                                                                        <span>
                                                                                                            <b>#{task.frontend_order}</b> - {task.title}
                                                                                                        </span>
                                                                                                        <MoreVertIcon className="options-icon-task-project-page" onClick={(event) => OpenContextMenuTask(event, task, list)} />
                                                                                                    </div>
                                                                                                </>
                                                                                            )}
                                                                                            <div className="dragging-task-dates-project-page">
                                                                                                {(task.start_date || task.due_date || task.end_date) && (
                                                                                                    <ScrollAwareTooltip title={getColorForTask(task).tooltip} arrow>
                                                                                                        <div className="dragging-task-tooltip-project-page">
                                                                                                            <div
                                                                                                                className="dragging-task-tooltip-content-project-page"
                                                                                                                style={{
                                                                                                                    backgroundColor: getColorForTask(task).backgroundColor,
                                                                                                                    color: getColorForTask(task).color
                                                                                                                }}>
                                                                                                                <AccessTimeIcon className="access-time-icon-project-page" />
                                                                                                                {task.end_date ? `${formatDate(task.end_date)}\u00A0` :
                                                                                                                    task.due_date ? `${formatDate(task.due_date)}\u00A0` :
                                                                                                                        task.start_date ? `${formatDate(task.start_date)}\u00A0` :
                                                                                                                            '\u00A0'}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </ScrollAwareTooltip>
                                                                                                )}
                                                                                                {task.has_description && (
                                                                                                    <div>
                                                                                                        <ScrollAwareTooltip title={t('project_page.task_desc')} arrow>
                                                                                                            <SubjectIcon />
                                                                                                        </ScrollAwareTooltip>
                                                                                                    </div>
                                                                                                )}
                                                                                                {task.has_attachments && (
                                                                                                    <ScrollAwareTooltip title={t('project_page.task_files')} arrow>
                                                                                                        <AttachFileIcon />
                                                                                                    </ScrollAwareTooltip>
                                                                                                )}
                                                                                                {task.has_comments && (
                                                                                                    <ScrollAwareTooltip title={t('project_page.comments')} arrow>
                                                                                                        <div className="checkbox-div-project-page">
                                                                                                            <MessageIcon className="message-icon-project-page" />
                                                                                                        </div>
                                                                                                    </ScrollAwareTooltip>
                                                                                                )}

                                                                                                {task.checklist_completion && task.checklist_completion.total > 0 && (
                                                                                                    <ScrollAwareTooltip title={t('project_page.checklist')} arrow>
                                                                                                        <div className="checkbox-div-project-page">
                                                                                                            <CheckBoxIcon className="checkbox-icon-project-page" /> {/* Dark-ish blue color */}
                                                                                                            <Typography variant="body2" className="checkbox-text-project-page">
                                                                                                                {`${task.checklist_completion.completed}/${task.checklist_completion.total}`}
                                                                                                            </Typography>
                                                                                                        </div>
                                                                                                    </ScrollAwareTooltip>
                                                                                                )}
                                                                                                <div className="members-avatar-div-project-page">
                                                                                                    <AvatarGroup
                                                                                                        max={Math.max(2, 4 - (task.has_description ? 1 : 0)
                                                                                                            - (task.has_comments ? 1 : 0)
                                                                                                            - ((task.checklist_completion && task.checklist_completion.total > 0) ? 1 : 0))}

                                                                                                        className="members-avatar-group-project-page"
                                                                                                    >
                                                                                                        {task.members?.map((memberId, index) => {
                                                                                                            // Find the full member data in workspaceWorkers based on the member ID
                                                                                                            const fullMember = workspaceWorkers?.find(worker => worker.id === memberId);

                                                                                                            // If the full member is found, render the Avatar with the member's info
                                                                                                            if (fullMember) {
                                                                                                                return (
                                                                                                                    <ScrollAwareTooltip key={`member-${index}-${memberId}`} title={fullMember.full_name || fullMember.username} arrow >
                                                                                                                        <Avatar
                                                                                                                            className="members-avatar-project-page"
                                                                                                                            src={`${fullMember.image}`}
                                                                                                                        />
                                                                                                                    </ScrollAwareTooltip>
                                                                                                                );
                                                                                                            }
                                                                                                            return null; // Return null if the member is not found (optional)
                                                                                                        })}
                                                                                                    </AvatarGroup>
                                                                                                </div>

                                                                                            </div>
                                                                                        </Paper>

                                                                                    </SortableItem>

                                                                                </div>
                                                                            ) : (
                                                                                //THIS HERE IS THE PLACEHOLDER!
                                                                                <div key={`placeholder-${list.id}-${index}`} className="task-placeholder-project-page">
                                                                                    < Paper
                                                                                        elevation={4}
                                                                                        className={`task-paper-project-page dragging-task-paper-project-page`}
                                                                                    >
                                                                                        <div className="dragging-task-title-project-page">
                                                                                            <b>#000</b> - {t('project_page.loading')}
                                                                                        </div>
                                                                                    </Paper>
                                                                                </div>
                                                                            )
                                                                        }
                                                                        )}
                                                                    </Box>
                                                                    {!collapsedLists[list.id] && (
                                                                        <div className="add-icon-project-page">
                                                                            <Button onClick={() => handleTaskModalOpen(list)} className="add-task-project-page" sx={{ color: getFontColor(list.color) }}>
                                                                                {t('project_page.add_card')} <AddIcon className="add-icon-font-project-page" sx={{ color: getFontColor(list.color) }} />
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                </CardContent>
                                                            </SortableContext>
                                                        </Box>
                                                    </SortableItem>
                                                ))}
                                            </SortableContext>
                                            <Box className="add-list-container" sx={{ ml: 2, mr: 4, minWidth: 250, backgroundColor: "#252224", borderRadius: "8px" }}>

                                                {openAddListModal ? (

                                                    <NewListModal

                                                        onAddList={(title) => {

                                                            handleAddList(title);

                                                        }}

                                                        onClose={handleCloseAddListModal}

                                                    />

                                                ) : (

                                                    <Button onClick={handleOpenAddListModal} sx={{ m: 1 }}>

                                                        <span> <Typography sx={{ color: "#deb887" }}>{t("project_page.add_list")}</Typography></span>
                                                        <AddIcon sx={{ color: "#C0C0C0", fontSize: "30px" }} />
                                                    </Button>
                                                )}
                                            </Box>
                                        </>
                                    )}
                                    <DragOverlay dropAnimation={{
                                        duration: 0,
                                        easing: 'linear',
                                        dragSourceOpacity: 1
                                    }}>
                                        {renderDragOverlay()}
                                    </DragOverlay>
                                </DndContext>
                            </div>
                        </div>
                    </div>
                    {openNewTaskModal && (
                        <NewTaskModal
                            openNewTaskModal={openNewTaskModal}
                            handleTaskModalClose={handleTaskModalClose}
                            handleSaveTask={handleSaveTask}
                            avoidMisfire={avoidMisfire}
                            DeleteLabel={DeleteLabel}
                            AddLabelToBoard={AddLabelToBoard}
                            UpdateLabel={UpdateLabel}
                            UpdateLabelTitle={UpdateLabelTitle}
                            myBoard={myBoard}
                            setMyBoard={setMyBoard}
                            workspace_id={myWorkspaces[0].id}
                        />
                    )}

                    {openArchiveTaskModal && (
                        <ArchiveTaskModal myTask={selectedTask} myBoardLabels={myBoard.labels} getColorForTask={getColorForTask}
                            openArchiveTaskModal={openArchiveTaskModal} formatDate={formatDate} workspaceWorkers={workspaceWorkers} handleArchiveTaskModalClose={handleArchiveTaskModalClose}
                            handleArchiveTask={handleArchiveTask} avoidMisfire={avoidMisfire} />
                    )}

                    {openArchiveListModal && (
                        <ArchiveListModal openArchiveListModal={openArchiveListModal}
                            handleArchiveListModalClose={handleArchiveListModalClose}
                            handleArchiveList={handleArchiveList} avoidMisfire={avoidMisfire} />
                    )}

                    {selectedTask && selectedTask.id && viewTaskModalOpen && (
                        <ViewTaskModal
                            openViewTaskModal={viewTaskModalOpen}
                            handleViewTaskModalClose={() => {
                                setViewTaskModalOpen(false);
                                setAutoFocusLabel(false);
                                setSelectedTask(null);
                            }}
                            task_id={selectedTask.id}
                            handleSaveTask={handleSaveTask}
                            List={selectedList}
                            myBoard={myBoard}
                            setMyBoard={setMyBoard}
                            updateToggleLabel={updateToggleLabel}
                            projectWorkers={projectWorkers}
                            myUserTimezone={myUserTimezone}
                            handleMarkAsDone={handleMarkAsDone}
                            handleMarkAsDoing={handleMarkAsDoing}
                            handleArchiveTask={handleArchiveTask}
                            avoidMisfire={avoidMisfire}
                            setContextTaskMenuPosition={setContextTaskMenuPosition}
                            setAutoFocusLabel={setAutoFocusLabel}
                            autoFocusLabel={autoFocusLabel}
                            openCover={openCover}
                            setCoverModalOpen={setCoverModalOpen}
                            workspace_id={myWorkspaces[0].id}
                            getCurrentlyLoggedUser={getCurrentlyLoggedUser}
                            workspaceWorkers={workspaceWorkers}
                            getColorForTask={getColorForTask}
                            formatDate={formatDate}

                        />
                    )}
                    <ListHeaderModal openListHeaderModal={openListHeaderModal} handleListHeaderModalClose={handleListHeaderModalClose} List={selectedList} workspace_id={myWorkspaces[0]?.id} project_id={myBoard?.project} />
                    {openListFiltersModal && (
                        <ListFiltersModal
                            openListFiltersModal={openListFiltersModal}
                            handleCloseFiltersModal={handleCloseFiltersModal}
                            list={selectedList}
                            project={myBoard?.project}
                            workspace_id={myWorkspaces[0]?.id}
                            filters={filters}
                            tempFilters={taskFilters}
                            labels={myBoard?.labels}
                            workers={workspaceWorkers}
                            currentSort={listSortState[selectedList.id]}
                        />
                    )}

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        PaperProps={{ className: "menu-actions-project-page" }}
                    >
                        <MenuItem className="menu-item-project-page" onClick={handleOpenFiltersModal}>
                            <span className="sorting-options-text-project-page">{t("project_page.filter_list")}</span>
                        </MenuItem>
                        <MenuItem className="menu-item-project-page" onClick={handleSubmenuOpen}>
                            <span className="sorting-options-text-project-page">{t("project_page.sort_list")}</span>
                        </MenuItem>
                    </Menu>
                    <Menu
                        anchorEl={submenuAnchorEl}
                        open={Boolean(submenuAnchorEl)}
                        onClose={handleSubmenuClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        PaperProps={{ className: "menu-actions-project-page" }}
                    >
                        <MenuItem
                            className="menu-item-project-page"
                            onClick={() => handleListSort('created_date')}>
                            {t("project_page.filter_created_date")}
                            <div className="sort-by-arrows">
                                <ArrowUpwardIcon
                                    className={`sort-by-arrow ${listSortState[selectedList.id] === 'created_date' ? 'highlighted-arrow' : ''
                                        }`}
                                />
                                <ArrowDownwardIcon
                                    className={`sort-by-arrow ${listSortState[selectedList.id] === '-created_date' ? 'highlighted-arrow' : ''
                                        }`}
                                />
                            </div>
                        </MenuItem>
                        <MenuItem
                            className="menu-item-project-page"
                            onClick={() => handleListSort('title')}>
                            {t("project_page.filter_title")}
                            <div className="sort-by-arrows">
                                <ArrowUpwardIcon
                                    className={`sort-by-arrow ${listSortState[selectedList.id] === 'title' ? 'highlighted-arrow' : ''
                                        }`}
                                />
                                <ArrowDownwardIcon
                                    className={`sort-by-arrow ${listSortState[selectedList.id] === '-title' ? 'highlighted-arrow' : ''
                                        }`}
                                />
                            </div>
                        </MenuItem>
                    </Menu>

                    <Menu
                        anchorReference="anchorPosition"
                        anchorPosition={
                            contextTaskMenuPosition.y !== 0 && contextTaskMenuPosition.x !== 0
                                ? { top: contextTaskMenuPosition.y, left: contextTaskMenuPosition.x }
                                : undefined
                        }
                        open={contextTaskMenuPosition.y !== 0 && contextTaskMenuPosition.x !== 0}
                        onClose={handleTaskCloseMenu}
                        PaperProps={{
                            className: "menu-actions-project-page"
                        }}
                    >
                        {taskMenuItems}
                    </Menu>

                    <Menu
                        anchorReference="anchorPosition"
                        anchorPosition={
                            contextListMenuPosition.y !== 0 && contextListMenuPosition.x !== 0
                                ? { top: contextListMenuPosition.y, left: contextListMenuPosition.x }
                                : undefined
                        }
                        open={contextListMenuPosition.y !== 0 && contextListMenuPosition.x !== 0}
                        onClose={handleListCloseMenu}
                        PaperProps={{
                            className: "menu-actions-project-page menu-actions-list-project-page"
                        }}
                    >
                        {listMenuItems}
                    </Menu>

                </>
            )
            }
        </>
    );
};

export default ProjectPage;